// Full-duplex voice for the StoreFront web chat — a port of the iOS
// VoiceSession state machine onto the Web Speech API.
//
//   idle → listening → thinking → speaking → listening …
//
//  • Listening: continuous SpeechRecognition with interim results; 1.2 s of
//    silence finalises the utterance and hands it to the chat.
//  • Thinking: an acknowledgement phrase is spoken; speech heard meanwhile is
//    classified — a cancel phrase aborts the turn, a real sentence is queued as
//    a follow-up, anything else gets one "didn't catch that" aside.
//  • Speaking: the reply is spoken sentence by sentence (gapless queue). New
//    speech that is NOT an echo of our own TTS, seen on two growing updates
//    and at least two words long, is a barge-in: TTS stops, the transcript
//    seeds the next utterance.
//
// The session never talks to the gateway itself: the ChatContext feeds it
// `beginThinking` / `speakReply` / `replyFailed` and receives `onSubmit`.

export type VoiceState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'paused';

export interface VoiceHandlers {
  onState: (state: VoiceState) => void;
  onTranscript: (text: string) => void;
  /** A finalised utterance to send to the chat. */
  onSubmit: (text: string) => void;
  /** The user cancelled while we were thinking / speaking. */
  onCancel: () => void;
  onError: (message: string) => void;
}

// ---- pure helpers (unit-tested) --------------------------------------------

const CANCEL_PHRASES = [
  'stop', 'cancel', 'cancel that', 'never mind', 'nevermind', 'forget it',
  'scratch that', 'hold on', 'wait', 'no wait', 'stop that', "don't",
];

export type ThinkingSpeech = 'cancel' | 'followUp' | 'unclear';

/** Same rules as iOS classifyThinkingSpeech. */
export function classifyThinkingSpeech(transcript: string): ThinkingSpeech {
  const t = transcript.toLowerCase().trim().replace(/[.,!?]/g, '');
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length <= 5) {
    for (const phrase of CANCEL_PHRASES) {
      if (t === phrase || t.startsWith(phrase + ' ') || t.endsWith(' ' + phrase) || t.includes(' ' + phrase + ' ')) {
        // "wait" / "hold on" / "don't" inside a longer request is content, not a cancel
        if (words.length <= 3 || (phrase !== 'wait' && phrase !== 'hold on' && phrase !== "don't")) return 'cancel';
      }
    }
  }
  return words.length >= 3 ? 'followUp' : 'unclear';
}

/** >60 % of the transcript's words appear in what we have been saying → echo. */
export function isLikelyEcho(transcript: string, ttsText: string): boolean {
  const norm = (s: string) => new Set(s.toLowerCase().replace(/[^a-z0-9' ]+/g, ' ').split(/\s+/).filter(Boolean));
  const tw = norm(transcript), sw = norm(ttsText);
  if (tw.size === 0) return false;
  let hit = 0;
  for (const w of tw) if (sw.has(w)) hit++;
  return hit / tw.size > 0.6;
}

/** Reply text → speakable sentences; the ```ui fence and markdown noise are dropped. */
export function extractSentences(text: string): string[] {
  const clean = text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/```[\s\S]*$/g, ' ')
    .replace(/[*_#`>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return [];
  return clean.split(/(?<=[.!?])\s+(?=[A-Z0-9"'(])/).map((s) => s.trim()).filter((s) => s.length > 0);
}

export function pickAckPhrase(transcript: string): string {
  const t = transcript.toLowerCase();
  const pool = /order|cart|add|confirm/.test(t)
    ? ['Working on your order.', 'Got it, adding that.']
    : ['Let me check on that.', 'One moment.'];
  return pool[Math.floor(Math.random() * pool.length)]!;
}

// ---- Web Speech API shims ----------------------------------------------------

interface SRAlternative { transcript: string; confidence: number }
interface SRResult { isFinal: boolean; length: number; [i: number]: SRAlternative }
interface SREvent { resultIndex: number; results: { length: number; [i: number]: SRResult } }
interface SRErrorEvent { error: string; message?: string }
interface SR {
  continuous: boolean; interimResults: boolean; lang: string; maxAlternatives: number;
  onresult: ((e: SREvent) => void) | null; onend: (() => void) | null; onerror: ((e: SRErrorEvent) => void) | null;
  onstart: (() => void) | null;
  start(): void; stop(): void; abort(): void;
}
type SRCtor = new () => SR;

function recognitionCtor(): SRCtor | null {
  const w = window as unknown as { SpeechRecognition?: SRCtor; webkitSpeechRecognition?: SRCtor };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function voiceSupported(): boolean {
  return typeof window !== 'undefined' && !!recognitionCtor() && 'speechSynthesis' in window;
}

// ---- the session -------------------------------------------------------------

const SILENCE_MS = 1200;
const BARGE_MIN_WORDS = 2;
const BARGE_MIN_CHARS = 3;
const BARGE_MIN_UPDATES = 2;
const UNCLEAR_GRACE_MS = 1200;

export class VoiceSession {
  private h: VoiceHandlers;
  private rec: SR | null = null;
  private active = false;
  private state: VoiceState = 'idle';
  private finalText = '';
  private interimText = '';
  private silenceTimer: number | null = null;
  private restartTimer: number | null = null;
  private voice: SpeechSynthesisVoice | null = null;

  // thinking-speech
  private pendingFollowUp = '';
  private unclearAsideSpoken = false;
  private unclearTimer: number | null = null;
  // speaking / barge-in
  private currentTTSText = '';
  private bargeUpdates = 0;
  private lastBargeTranscript = '';
  private queued = 0;
  private replyDone = false;

  constructor(handlers: VoiceHandlers) { this.h = handlers; }

  get currentState(): VoiceState { return this.state; }
  get isActive(): boolean { return this.active; }

  /** Must be called from a user gesture (Safari unlocks speechSynthesis on it). */
  start(): void {
    if (this.active) return;
    const Ctor = recognitionCtor();
    if (!Ctor || !('speechSynthesis' in window)) { this.h.onError('Voice is not supported in this browser — try Safari or Chrome.'); return; }
    this.active = true;
    this.pickVoice();
    // Unlock TTS on the gesture with a silent utterance.
    try { const u = new SpeechSynthesisUtterance(' '); u.volume = 0; window.speechSynthesis.speak(u); } catch { /* ignore */ }
    this.setState('listening');
    this.startRecognition();
  }

  stop(): void {
    this.active = false;
    this.clearTimers();
    this.stopSpeaking();
    this.stopRecognition();
    this.finalText = ''; this.interimText = '';
    this.h.onTranscript('');
    this.setState('idle');
  }

  /** The chat accepted the utterance: speak an ack and wait for the reply. */
  beginThinking(transcript: string): void {
    if (!this.active) return;
    this.pendingFollowUp = '';
    this.unclearAsideSpoken = false;
    this.bargeUpdates = 0; this.lastBargeTranscript = '';
    this.finalText = ''; this.interimText = '';
    this.setState('thinking');
    this.restartRecognition();
    const ack = pickAckPhrase(transcript);
    this.currentTTSText = ack;
    this.speak(ack, false);
  }

  /** The full reply arrived: speak it, then go back to listening. */
  speakReply(text: string): void {
    if (!this.active || this.state !== 'thinking') return;
    const sentences = extractSentences(text);
    if (sentences.length === 0) { this.afterSpeaking(); return; }
    this.setState('speaking');
    this.replyDone = false;
    this.currentTTSText = sentences.join(' ');
    this.queued = sentences.length;
    for (const s of sentences) this.speak(s, true);
    this.replyDone = true;
  }

  /** The chat turn failed or was cancelled — resume listening. */
  replyFailed(): void {
    if (!this.active) return;
    this.stopSpeaking();
    this.afterSpeaking();
  }

  // ---- recognition -----------------------------------------------------------

  private startRecognition(): void {
    const Ctor = recognitionCtor();
    if (!Ctor || !this.active) return;
    this.stopRecognition();
    const rec = new Ctor();
    rec.continuous = true; rec.interimResults = true; rec.lang = 'en-US'; rec.maxAlternatives = 1;
    rec.onresult = (e) => this.onResult(e);
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        this.h.onError('Microphone access was denied.');
        this.stop();
      }
      // 'no-speech' / 'aborted' / 'network' → onend restarts us
    };
    rec.onend = () => {
      if (this.rec !== rec) return;
      if (this.active) this.scheduleRestart();
    };
    this.rec = rec;
    try { rec.start(); } catch { this.scheduleRestart(); }
  }

  private stopRecognition(): void {
    if (this.restartTimer) { clearTimeout(this.restartTimer); this.restartTimer = null; }
    const rec = this.rec; this.rec = null;
    if (rec) { rec.onresult = null; rec.onend = null; rec.onerror = null; try { rec.abort(); } catch { /* ignore */ } }
  }

  private restartRecognition(): void { this.startRecognition(); }

  private scheduleRestart(): void {
    if (this.restartTimer || !this.active) return;
    this.restartTimer = window.setTimeout(() => { this.restartTimer = null; if (this.active) this.startRecognition(); }, 250);
  }

  private onResult(e: SREvent): void {
    let interim = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i]!;
      const t = r[0]?.transcript ?? '';
      if (r.isFinal) this.finalText = (this.finalText + ' ' + t).trim();
      else interim += t;
    }
    this.interimText = interim.trim();
    const transcript = (this.finalText + ' ' + this.interimText).trim();
    if (!transcript) return;

    switch (this.state) {
      case 'listening':
        this.h.onTranscript(transcript);
        this.resetSilenceTimer();
        break;
      case 'speaking':
        this.checkBargeIn(transcript);
        break;
      case 'thinking':
        this.checkThinkingSpeech(transcript);
        break;
      default:
        break;
    }
  }

  private resetSilenceTimer(): void {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.silenceTimer = window.setTimeout(() => { this.silenceTimer = null; this.finalize(); }, SILENCE_MS);
  }

  private finalize(): void {
    if (this.state !== 'listening') return;
    const text = (this.finalText + ' ' + this.interimText).trim();
    if (!text) return;
    this.finalText = ''; this.interimText = '';
    this.h.onTranscript('');
    this.h.onSubmit(text);
  }

  // ---- thinking-speech -------------------------------------------------------

  private checkThinkingSpeech(transcript: string): void {
    if (isLikelyEcho(transcript, this.currentTTSText)) return;
    const kind = classifyThinkingSpeech(transcript);
    if (kind === 'cancel') {
      this.finalText = ''; this.interimText = '';
      this.stopSpeaking();
      this.h.onCancel();
      this.afterSpeaking();
      return;
    }
    if (kind === 'followUp') {
      this.pendingFollowUp = transcript;
      if (this.unclearTimer) { clearTimeout(this.unclearTimer); this.unclearTimer = null; }
      return;
    }
    // unclear: give the recogniser a moment to turn two words into a sentence, then say so once
    if (!this.unclearAsideSpoken && !this.unclearTimer) {
      this.unclearTimer = window.setTimeout(() => {
        this.unclearTimer = null;
        if (this.state !== 'thinking' || this.unclearAsideSpoken || this.pendingFollowUp) return;
        this.unclearAsideSpoken = true;
        const aside = "Sorry, I didn't catch that — still working on your request.";
        this.currentTTSText = (this.currentTTSText + ' ' + aside).trim();
        this.speak(aside, false);
      }, UNCLEAR_GRACE_MS);
    }
  }

  // ---- speaking / barge-in ---------------------------------------------------

  private checkBargeIn(transcript: string): void {
    if (transcript.length < BARGE_MIN_CHARS) { this.bargeUpdates = 0; this.lastBargeTranscript = ''; return; }
    if (isLikelyEcho(transcript, this.currentTTSText)) { this.bargeUpdates = 0; this.lastBargeTranscript = ''; return; }
    if (transcript !== this.lastBargeTranscript && transcript.length > this.lastBargeTranscript.length) {
      this.bargeUpdates += 1;
      this.lastBargeTranscript = transcript;
    }
    const words = transcript.split(/\s+/).filter(Boolean).length;
    if (this.bargeUpdates >= BARGE_MIN_UPDATES && words >= BARGE_MIN_WORDS) {
      this.stopSpeaking();
      this.bargeUpdates = 0; this.lastBargeTranscript = '';
      // Seed the next utterance with what we heard and keep listening.
      this.currentTTSText = '';
      this.setState('listening');
      this.h.onTranscript(transcript);
      this.resetSilenceTimer();
    }
  }

  private speak(text: string, isReply: boolean): void {
    const u = new SpeechSynthesisUtterance(text);
    if (this.voice) u.voice = this.voice;
    u.rate = 1.0; u.pitch = 1.0; u.volume = 1.0;
    if (isReply) {
      const done = () => { this.queued = Math.max(0, this.queued - 1); if (this.queued === 0 && this.replyDone && this.state === 'speaking') this.afterSpeaking(); };
      u.onend = done; u.onerror = done;
    }
    window.speechSynthesis.speak(u);
  }

  private stopSpeaking(): void {
    this.queued = 0;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
  }

  /** Reply spoken (or abandoned): a queued follow-up goes straight out, else listen. */
  private afterSpeaking(): void {
    if (!this.active) return;
    this.currentTTSText = '';
    this.finalText = ''; this.interimText = '';
    this.bargeUpdates = 0; this.lastBargeTranscript = '';
    const follow = this.pendingFollowUp.trim();
    this.pendingFollowUp = '';
    this.setState('listening');
    this.restartRecognition();
    if (follow) this.h.onSubmit(follow);
  }

  private setState(s: VoiceState): void {
    if (this.state === s) return;
    this.state = s;
    this.h.onState(s);
  }

  private clearTimers(): void {
    if (this.silenceTimer) { clearTimeout(this.silenceTimer); this.silenceTimer = null; }
    if (this.unclearTimer) { clearTimeout(this.unclearTimer); this.unclearTimer = null; }
    if (this.restartTimer) { clearTimeout(this.restartTimer); this.restartTimer = null; }
  }

  private pickVoice(): void {
    const choose = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) return;
      const preferred = ['Samantha', 'Google US English', 'Microsoft Aria Online', 'Microsoft Jenny Online', 'Karen', 'Daniel'];
      const en = voices.filter((v) => /^en[-_]/i.test(v.lang));
      this.voice = preferred.map((n) => en.find((v) => v.name.startsWith(n))).find(Boolean)
        || en.find((v) => v.lang === 'en-US') || en[0] || null;
    };
    choose();
    if (!this.voice) window.speechSynthesis.onvoiceschanged = () => choose();
  }
}
